import { useEffect, useState } from "react";
import { IObject } from "../../../../shared/utils/utils";
import { ITableRowData } from "../../data-table/data-table";
import DatamodelFieldModal from "../widget-modals/datamodel-field-modal/datamodel-field-modal";
import DatamodelRelatedAlertedEventModal from "../widget-modals/datamodel-related-alerted-event-modal/datamodel-related-alerted-event-modal";
import DatamodelRelatedEntityModal from "../widget-modals/datamodel-related-entity-modal/datamodel-related-entity-modal";
import DatamodelRelatedEventModal from "../widget-modals/datamodel-related-event-modal/datamodel-related-event-modal";
import DatamodelTableModal from "../widget-modals/datamodel-table-modal/datamodel-table-modal";
import TextWidgetModal from "../widget-modals/text-widget-modal/text-widget-modal";
import { IWidgetModal, Widget } from "../widget-utils";
import WidgetsLayout, { IWidgetLayoutConfig } from "../widgets-layout";
import "./widget-component.scss";
import ReferenceComponentFieldModal from "../widget-modals/reference-component-modal/reference-component-modal";

export interface IWidgetLayout {
  layout: IWidgetLayoutConfig[];
  layoutItems: Widget[];
}

interface IWidgetComponentProps {
  layout: IWidgetLayout;
  widgets: IWidgetModal[];
  readonly?: boolean;
  data?: IObject;

  handleLayoutChange(layout: IWidgetLayout): void;
  onDataChanged?(newValue: string, key?: string): void
}

const WidgetComponent = ({layout: componentLayout, widgets, readonly = false, data, handleLayoutChange, onDataChanged}: IWidgetComponentProps) => {
  const [layout, setLayout] = useState<IWidgetLayout>(componentLayout);

  const [widgetData, setWidgetData] = useState<ITableRowData>({id: ""});

  useEffect(() => {
    setLayout(componentLayout);
  }, [componentLayout]);

  const onWidgetChanged = (widget: Widget | undefined) => {
    if (!widget) {
      setWidgetData({id: ""});
      return; // Modal closed without changes
    }

    const widgetExists = layout.layoutItems.some(w => w.id === widget.id);

    if (widgetExists) {
      const layoutCopy = {...layout};
      layoutCopy.layoutItems = layout.layoutItems.map(w => {
        if (w.id === widget.id) {
          return {...w, ...widget};
        }
        return w;
      });
      setLayout(layoutCopy);
      handleLayoutChange(layoutCopy);
    } else {
      const addedLayout: IWidgetLayoutConfig = {
        i: widget.id,
        h: 2,
        w: 6,
        minW: 1,
        minH: 1,
        x: 0,
        y: Math.max(0, ...layout.layout.map(item => item.y + item.h)) // find the vertical end of the layout
      };
      const newLayout = {
        layout: [...layout.layout, addedLayout],
        layoutItems: [...layout.layoutItems, widget]
      };
      setLayout(newLayout);
      handleLayoutChange(newLayout);
    }

    setWidgetData({id: ""});
  };

  const onLayoutChanged = (changedLayout: IWidgetLayoutConfig[]) => {
    const newLayout = {
      layout: changedLayout,
      layoutItems: layout.layoutItems.filter(widget => changedLayout.some(l => l.i === widget.id))
    };
    setLayout(newLayout);
    handleLayoutChange(newLayout);
  };

  const renderWidgetModals = () => {
    const components = widgets.map(widget => {
      const modalData = widget.type === widgetData.modalType ? {...widgetData} : {id: ""};
      switch (widget.type) {
        case "text":
          return <TextWidgetModal modalOpen={widgetData.modalType === widget.type} widgetData={modalData} onWidgetChanged={onWidgetChanged}/>;
        case "datamodelField":
          return <DatamodelFieldModal datamodelType={widget.datamodelType} selectedDatamodelId={widget.datamodelId} 
            modalOpen={widget.type === widgetData.modalType && widget.datamodelType === widgetData.modalData.datamodelType} 
            widgetData={modalData} onWidgetChanged={onWidgetChanged}/>;
        case "reference":
          return <ReferenceComponentFieldModal datamodelType={widget.datamodelType} selectedDatamodelId={widget.datamodelId} 
            modalOpen={widget.type === widgetData.modalType && widget.datamodelType === widgetData.modalData.datamodelType} 
            widgetData={modalData} onWidgetChanged={onWidgetChanged}/>;
        case "datamodelTable":
          return <DatamodelTableModal modalOpen={widget.type === widgetData.modalType} widgetData={modalData} onWidgetChanged={onWidgetChanged} />;
        case "datamodelRelatedEntity":
          return <DatamodelRelatedEntityModal modalOpen={widget.type === widgetData.modalType && widget.type === widgetData.modalType} widgetData={modalData} onWidgetChanged={onWidgetChanged} />;
        case "datamodelRelatedEvent":
          return <DatamodelRelatedEventModal modalOpen={widget.type === widgetData.modalType && widget.type === widgetData.modalType} widgetData={modalData} onWidgetChanged={onWidgetChanged} />;
        case "datamodelRelatedAlertedEvent":
          return <DatamodelRelatedAlertedEventModal modalOpen={widget.type === widgetData.modalType && widget.type === widgetData.modalType} widgetData={modalData} onWidgetChanged={onWidgetChanged} />;
        } 
    });
    return <div className="widgetComponent-buttonGroup">{components}</div>;
  };

  const onWidgetEdit = (widget: Widget) => {
    setWidgetData({...widget});
  };

  return <div className="widgetComponent">
    {!readonly && renderWidgetModals()}
    <WidgetsLayout
      layout={layout}
      readonly={readonly}
      onLayoutChanged={onLayoutChanged}
      onWidgetEdit={onWidgetEdit}
      data={data}
      onDataChanged={onDataChanged}
    />
  </div>;
};

export default WidgetComponent;
